import React, { useState, useEffect, useCallback } from 'react';

import {
  Users, Clock, MapPin, Filter, Search,
  Loader2, User, ShieldCheck, BadgeCheck, MessageSquare, RotateCcw, X
} from 'lucide-react';
import { friendsApi, type FriendProfileDoc } from '@/services/friendsApi';
import { locationData } from '@/lib/indianLocations';
import SEO from '@/components/common/SEO';
import { Link } from 'react-router-dom';
import { getAvatarUrl } from '@/lib/utils';

type CollectionTab = 'just-joined' | 'nearby' | 'search';

interface FriendFilters {
  gender?: string;
  ageMin?: number;
  ageMax?: number;
  state?: string;
  city?: string;
  isVerified?: boolean;
}

const FindFriendsPage: React.FC = () => {
  const [tab, setTab] = useState<CollectionTab>('just-joined');
  const [profiles, setProfiles] = useState<FriendProfileDoc[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);

  // Filters
  const [filters, setFilters] = useState<FriendFilters>({});
  const [showFilters, setShowFilters] = useState(true);

  // Nearby
  const [userLat, setUserLat] = useState<number | null>(null);
  const [userLon, setUserLon] = useState<number | null>(null);

  useEffect(() => {
    // Get user location
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => { setUserLat(pos.coords.latitude); setUserLon(pos.coords.longitude); },
        () => { }
      );
    }
  }, []);

  const loadCollection = useCallback(async () => {
    setLoading(true);
    try {
      // Build clean filter parameters
      const cleanParams: Record<string, any> = {};
      if (filters.gender) cleanParams.gender = filters.gender;
      if (filters.ageMin !== undefined && filters.ageMin !== null) cleanParams.ageMin = filters.ageMin;
      if (filters.ageMax !== undefined && filters.ageMax !== null) cleanParams.ageMax = filters.ageMax;
      if (filters.state) cleanParams.state = filters.state;
      if (filters.city) cleanParams.city = filters.city;
      if (filters.isVerified) cleanParams.isVerified = true;

      let result;
      switch (tab) {
        case 'just-joined':
          result = await friendsApi.getJustJoined(page, 12, cleanParams);
          break;

        case 'nearby':
          if (userLat && userLon) {
            result = await friendsApi.getNearby(userLat, userLon, 50, page, 12, cleanParams);
          } else {
            result = await friendsApi.search({ ...cleanParams, page, size: 12 });
          }
          break;
        case 'search':
          result = await friendsApi.search({ ...cleanParams, page, size: 12 });
          break;
        default:
          result = await friendsApi.getJustJoined(page, 12, cleanParams);
      }

      let fetchedProfiles: FriendProfileDoc[] = result?.profiles || [];
      let totalHits = result?.totalHits ?? (result as any)?.total ?? fetchedProfiles.length;

      // Defensive client-side verification check
      if (filters.isVerified) {
        const verifiedProfiles = fetchedProfiles.filter(p => Boolean(p.isVerified || (p as any).verified));
        // If client-side filter removed unverified items from the current page, adjust totalHits proportionally
        if (verifiedProfiles.length < fetchedProfiles.length) {
          totalHits = Math.max(0, totalHits - (fetchedProfiles.length - verifiedProfiles.length));
        }
        fetchedProfiles = verifiedProfiles;
      }

      setProfiles(fetchedProfiles);
      setTotal(totalHits);
    } catch (e) {
      setProfiles([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [tab, page, filters, userLat, userLon]);

  useEffect(() => { loadCollection(); }, [loadCollection]);

  const handleSearch = () => {
    setPage(0);
    loadCollection();
  };

  const handleClearFilters = () => {
    setFilters({});
    setPage(0);
  };

  const hasActiveFilters = Boolean(
    filters.gender || filters.ageMin || filters.ageMax || filters.state || filters.city || filters.isVerified
  );

  const tabs = [
    { id: 'just-joined' as const, label: 'New Friends', icon: Clock, color: 'text-blue-600 bg-blue-50' },
    { id: 'nearby' as const, label: 'Nearby', icon: MapPin, color: 'text-green-600 bg-green-50' },
  ];

  const inputClass = "w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20";
  const selectClass = "w-full px-3 py-2 rounded-lg border border-border bg-background text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 cursor-pointer";

  return (
    <div className="min-h-screen bg-background py-6 px-4 sm:px-6 lg:px-8">
      <SEO title="Find Friends" description="Connect with platonic friends in your area sharing common interests." />

      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <Users className="h-8 w-8 text-primary" />
            Find Friends
          </h1>
          <p className="text-muted-foreground mt-1">Connect with like-minded people in your area</p>
        </div>

        {/* Collection Tabs */}
        <div className="flex flex-wrap gap-3 mb-6 items-center">
          {tabs.map(t => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); setPage(0); }}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition ${tab === t.id ? 'bg-primary text-primary-foreground shadow' : 'border border-border hover:bg-secondary'
                }`}
            >
              <t.icon className="h-4 w-4" />
              {t.label}
            </button>
          ))}

          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-secondary transition ml-auto ${showFilters ? 'bg-secondary text-foreground' : ''
              }`}
          >
            <Filter className="h-4 w-4" />
            Filters {hasActiveFilters && <span className="h-2 w-2 rounded-full bg-emerald-600 inline-block" />}
          </button>
        </div>

        {/* Active Filter Badges */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs text-muted-foreground font-medium">Active filters:</span>
            {filters.isVerified && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                Aadhaar Verified Only
                <button
                  type="button"
                  onClick={() => setFilters(f => ({ ...f, isVerified: undefined }))}
                  className="ml-1 hover:text-emerald-900 dark:hover:text-emerald-100"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.gender && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-secondary text-secondary-foreground border border-border">
                Gender: {filters.gender}
                <button type="button" onClick={() => setFilters(f => ({ ...f, gender: undefined }))} className="ml-1">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {(filters.ageMin || filters.ageMax) && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-secondary text-secondary-foreground border border-border">
                Age: {filters.ageMin || 18} - {filters.ageMax || 99}
                <button type="button" onClick={() => setFilters(f => ({ ...f, ageMin: undefined, ageMax: undefined }))} className="ml-1">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.state && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-secondary text-secondary-foreground border border-border">
                State: {filters.state}
                <button type="button" onClick={() => setFilters(f => ({ ...f, state: undefined, city: undefined }))} className="ml-1">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            {filters.city && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-secondary text-secondary-foreground border border-border">
                City: {filters.city}
                <button type="button" onClick={() => setFilters(f => ({ ...f, city: undefined }))} className="ml-1">
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}
            <button
              type="button"
              onClick={handleClearFilters}
              className="text-xs text-muted-foreground hover:text-foreground underline ml-2"
            >
              Clear all
            </button>
          </div>
        )}

        {/* Filter Panel */}
        {showFilters && (
          <div className="mb-6 p-5 rounded-2xl border border-border bg-card shadow-sm">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Gender</label>
                <select
                  className={selectClass}
                  value={filters.gender || ''}
                  onChange={e => {
                    setFilters(f => ({ ...f, gender: e.target.value || undefined }));
                    setPage(0);
                  }}
                >
                  <option value="">Any</option>
                  <option value="FEMALE">Female</option>
                  <option value="MALE">Male</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Age from</label>
                <input
                  type="number"
                  min={18}
                  max={99}
                  className={inputClass}
                  value={filters.ageMin ?? ''}
                  onChange={e => {
                    setFilters(f => ({ ...f, ageMin: e.target.value ? Number(e.target.value) : undefined }));
                    setPage(0);
                  }}
                  placeholder="18"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Age to</label>
                <input
                  type="number"
                  min={18}
                  max={99}
                  className={inputClass}
                  value={filters.ageMax ?? ''}
                  onChange={e => {
                    setFilters(f => ({ ...f, ageMax: e.target.value ? Number(e.target.value) : undefined }));
                    setPage(0);
                  }}
                  placeholder="99"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">State</label>
                <select
                  className={selectClass}
                  value={filters.state || ''}
                  onChange={e => {
                    setFilters(f => ({ ...f, state: e.target.value || undefined, city: undefined }));
                    setPage(0);
                  }}
                >
                  <option value="">Any</option>
                  {locationData.map(s => <option key={s.code} value={s.state}>{s.state}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">City</label>
                <select
                  className={selectClass}
                  value={filters.city || ''}
                  disabled={!filters.state}
                  onChange={e => {
                    setFilters(f => ({ ...f, city: e.target.value || undefined }));
                    setPage(0);
                  }}
                >
                  <option value="">Any</option>
                  {filters.state && (locationData.find(s => s.state === filters.state)?.cities || []).map(c =>
                    <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="col-span-2 sm:col-span-3 md:col-span-4 lg:col-span-5 flex items-center pt-2">
                <label className="flex items-center gap-2 cursor-pointer text-xs font-semibold text-foreground select-none p-2 rounded-xl border border-emerald-200 bg-emerald-50/50 dark:border-emerald-900/50 dark:bg-emerald-950/20 hover:bg-emerald-100/50 transition">
                  <input
                    type="checkbox"
                    checked={!!filters.isVerified}
                    onChange={e => {
                      const checked = e.target.checked;
                      setFilters(f => ({ ...f, isVerified: checked ? true : undefined }));
                      setPage(0);
                    }}
                    className="h-4 w-4 rounded border-border text-emerald-600 focus:ring-emerald-500 cursor-pointer accent-emerald-600"
                  />
                  <span className="flex items-center gap-1.5 text-emerald-800 dark:text-emerald-300 font-semibold text-sm">
                    <ShieldCheck className="h-4.5 w-4.5 text-emerald-600 dark:text-emerald-400" />
                    Aadhaar Verified Members Only
                  </span>
                </label>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-3">
              <button
                onClick={handleSearch}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary text-primary-foreground font-semibold text-sm hover:brightness-110 transition shadow-sm"
              >
                <Search className="h-4 w-4" />
                Search Friends
              </button>
              {hasActiveFilters && (
                <button
                  onClick={handleClearFilters}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-muted-foreground hover:text-foreground text-sm font-medium transition hover:bg-secondary"
                >
                  <RotateCcw className="h-4 w-4" />
                  Reset Filters
                </button>
              )}
            </div>
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground border border-dashed border-border rounded-2xl p-8 bg-card/50">
            <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p className="text-lg font-medium text-foreground">
              {filters.isVerified ? 'No Aadhaar-verified friends found' : 'No friends found'}
            </p>
            <p className="text-sm mt-1 max-w-md mx-auto">
              {filters.isVerified
                ? 'There are currently no Aadhaar-verified members matching your criteria. Try adjusting your filters or search across all members.'
                : 'Try adjusting your filters or checking back later.'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-secondary text-foreground text-sm font-medium hover:bg-secondary/80 transition"
              >
                <RotateCcw className="h-4 w-4" />
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{total}</span> {total === 1 ? 'friend' : 'friends'} found
                {filters.isVerified && <span className="ml-1 text-emerald-600 font-medium">(Aadhaar Verified Only)</span>}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {profiles.map(p => (
                <FriendCard key={p.userId} profile={p} />
              ))}
            </div>
            {/* Pagination */}
            {total > 12 && (
              <div className="flex justify-center gap-3 mt-8">
                <button disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}
                  className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-secondary disabled:opacity-50">
                  Previous
                </button>
                <button disabled={(page + 1) * 12 >= total} onClick={() => setPage(p => p + 1)}
                  className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-secondary disabled:opacity-50">
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const FriendCard: React.FC<{ profile: FriendProfileDoc }> = ({ profile }) => {
  const [imgError, setImgError] = useState(false);
  const avatarSrc = getAvatarUrl(profile.avatarUrl);
  const isVerified = Boolean(profile.isVerified || (profile as any).verified);

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden hover:shadow-md transition flex flex-col group cursor-pointer h-full">
      {/* Avatar */}
      <div className="h-36 bg-gradient-to-br from-indigo-100 to-purple-50 flex items-center justify-center relative overflow-hidden">
        {avatarSrc && !imgError ? (
          <img
            src={avatarSrc}
            alt={profile.name || 'User'}
            className="h-full w-full object-cover object-top"
            onError={() => setImgError(true)}
          />
        ) : (
          <User className="h-16 w-16 text-indigo-300" />
        )}
      </div>

      <div className="p-4 flex flex-col flex-grow">
        <div className="flex items-center justify-between gap-1.5 mb-1.5">
          <h3 className="font-bold text-foreground truncate">{profile.name || 'Anonymous'}</h3>
          {isVerified && (
            <span
              title="Aadhaar Verified Member"
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800 flex-shrink-0"
            >
              <BadgeCheck className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              Aadhaar Verified
            </span>
          )}
        </div>

        <div className="space-y-1 text-sm text-muted-foreground mb-3 flex-grow">
          {profile.age && <span>{profile.age} yrs</span>}
          {profile.city && <span className="ml-2 flex items-center gap-0.5 inline-flex"><MapPin className="h-3 w-3" />{profile.city}</span>}

          {profile.bio && (
            <p className="mt-2 text-xs line-clamp-2 text-foreground/80">{profile.bio}</p>
          )}

          {profile.interests && profile.interests.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {profile.interests.slice(0, 3).map(interest => (
                <span key={interest} className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] bg-secondary text-secondary-foreground border border-border">
                  {interest}
                </span>
              ))}
              {profile.interests.length > 3 && (
                <span className="inline-flex px-1.5 py-0.5 rounded-full text-[10px] bg-secondary text-secondary-foreground border border-border">
                  +{profile.interests.length - 3}
                </span>
              )}
            </div>
          )}
        </div>

        <Link to={`/messages?userId=${profile.userId}&name=${encodeURIComponent(profile.name || 'Friend')}${profile.avatarUrl ? `&avatar=${encodeURIComponent(profile.avatarUrl)}` : ''}`} className="w-full flex items-center justify-center gap-2 py-2 mt-auto border border-primary/20 rounded-lg text-primary text-sm font-medium hover:bg-primary/5 transition">
          <MessageSquare className="h-4 w-4" /> Say Hi
        </Link>
      </div>
    </div>
  );
};

export default FindFriendsPage;
